/**
 * Friendly full-screen panel shown when a record is no longer awaiting
 * annotation (annotated_both / agreed / disputed / finalised) — terminal-
 * ish states that aren't errors but where the labelling UI can't proceed.
 */
import React from 'react';

import { Button } from '../../../../../ui/button';
import type { GoLabDatasetRecordStatus } from '../../types';

import { FullScreen } from './AnnotationFlowChrome';


const FRIENDLY_TERMINAL_COPY: Partial<
  Record<GoLabDatasetRecordStatus, { title: string; body: string }>
> = {
  annotated_both: {
    title: "This record's annotation is complete.",
    body: 'Both annotators have submitted. The agreement check happens automatically; the record will appear on the Review tab if it needs sign-off.',
  },
  agreed: {
    title: 'This record is awaiting manager sign-off.',
    body: 'Both annotators agreed on the labels. A manager will promote it to the golden dataset from the Review tab.',
  },
  disputed: {
    title: 'This record is awaiting adjudication.',
    body: 'The two annotations disagreed. A manager will resolve the dispute from the Review tab.',
  },
  finalised: {
    title: 'This record is already in the golden dataset.',
    body: "It's finalised and no longer in the annotation pipeline.",
  },
};


interface Props {
  status: GoLabDatasetRecordStatus;
  onBack: () => void;
}


const RecordCompleteScreen: React.FC<Props> = ({ status, onBack }) => {
  const copy = FRIENDLY_TERMINAL_COPY[status];
  // Fall back to neutral copy if we ever land here with an unmapped
  // status — better than rendering a raw enum value.
  const title = copy?.title ?? 'This record is no longer awaiting your annotation.';
  const body =
    copy?.body ??
    'It has moved past the annotation step. Head back to the queue to pick up another record.';
  return (
    <FullScreen>
      <div
        className="flex max-w-md flex-col items-start gap-3 rounded-2xl border bg-background p-6"
        data-testid="record-complete-panel"
      >
        <h2 className="font-jakarta text-lg font-extrabold tracking-[-0.02em] text-foreground">
          {title}
        </h2>
        <p className="text-[13px] leading-[1.6] text-muted-foreground">
          {body}
        </p>
        <Button variant="outline" onClick={onBack}>
          ← Back to queue
        </Button>
      </div>
    </FullScreen>
  );
};

export default RecordCompleteScreen;
